import { toast } from "@/components/common/Toast/Toast"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { notifyManager } from "@tanstack/react-query"
import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { useVerifyStatement } from "../../../../../api/declarationsApi"
import type { WorklistIssue } from "../../studio/cellState"
import { BusyLabel } from "../footer/BusyLabel"
import { signStep3 } from "../footer/useConformFramework"
import { useConditionRows, useReviewMode } from "../reviewMode"
import type { StatementCard } from "../useStudioV2State"
import { SubstanceEditor } from "./SubstanceEditor"
import {
  useConditionFieldWrites,
  type ConditionFieldWrites,
} from "./useConditionFieldWrites"

/** The focused step that takes over the review-mask pane for the STOFFE row
 *  (review-mask prototype, round 6): the substance table, beside the
 *  still-live PDF. */
export type SubstanceSheet = {
  kind: "substances"
  cardId: string
  /** The chip the reviewer clicked: that substance's per-article rows start
   *  open. Null: the row's plain edit button. */
  focusKey: string | null
}

export interface SubstanceEditorSheetProps {
  declId: string
  card: StatementCard
  /** The card's position among the Verordnung's cards: the header names the
   *  statement ("Aussage N") because the card itself is no longer on screen. */
  index: number
  poolEmpty: boolean
  articleNames: Map<string, string>
  focusKey: string | null
  onClose: () => void
  /** This card's own step-3 worklist issue (W5 step3-chip): gates the header's
   *  "Bestätigen" button, enabled only while `state === "needs-verification"`.
   *  Undefined (every caller/test predating this prop, and a card with no open
   *  step-3 issue at all): the button renders disabled with the "nothing to
   *  confirm" hint, same as a `ready` issue (see `ConfirmStep3Button`'s own
   *  doc, below in this file). */
  step3Issue?: WorklistIssue
}

/**
 * Hosts `SubstanceEditor` in place of the statement cards (the same
 * `sv2-sheet` takeover as `ArticleBrowserSheet`) rather than in
 * `SubstanceRow`'s old blocking dialog: the document pane stays live beside
 * it, so the reviewer reads a value off the PDF and types it into the table
 * without closing anything (the whole point of the side-by-side workspace).
 * Every write inside is an immediate per-field commit
 * (`useConditionFieldWrites`), so "Fertig" only closes the sheet.
 *
 * "Bestätigen" (W5 step3-chip, product ask 2026-09-17) is the per-card twin
 * of "Verordnung als geprüft markieren": it signs THIS card's own step-3
 * articles the same way, through the extracted `signStep3` (no second copy
 * of the marks/merge/PUT, see that function's own doc).
 */
export function SubstanceEditorSheet({
  declId,
  card,
  index,
  poolEmpty,
  articleNames,
  focusKey,
  onClose,
  step3Issue,
}: SubstanceEditorSheetProps) {
  const { translate } = useTranslation()
  // ONE write machinery for the whole sheet: the editor saves through it and
  // "Bestätigen" rides its save chain (see `ConfirmStep3Button`).
  const writes = useConditionFieldWrites(declId, card)
  return (
    <div
      className="sv2-sheet"
      data-testid="substance-editor"
      onKeyDown={(e) => {
        // Escape closes THIS sheet, never the whole workspace (same contract
        // as `ArticleBrowser`'s own Esc).
        if (e.key !== "Escape") return
        e.stopPropagation()
        onClose()
      }}
    >
      <div className="sv2-sshead">
        <span className="sv2-ssnum">
          {translate("MC_DECL_V2_UNIT_LABEL", { i: index + 1 })}
        </span>
        <span className="sv2-sstitle">
          {translate("MC_DECL_V2_UNIT_SUBSTANCES_DIALOG_TITLE")}
        </span>
        <ConfirmStep3Button
          declId={declId}
          framework={card.framework}
          step3Issue={step3Issue}
          signAfterSaves={writes.signAfterSaves}
        />
        <button type="button" className="sv2-hact" onClick={onClose}>
          {translate("MC_DECL_V2_SUBST_SHEET_DONE")}
        </button>
      </div>
      <div className="sv2-ssbody">
        <SubstanceEditor
          declId={declId}
          card={card}
          poolEmpty={poolEmpty}
          articleNames={articleNames}
          focusKey={focusKey}
          writes={writes}
        />
      </div>
    </div>
  )
}

/**
 * THE PER-CARD STEP-3 CONFIRM. Enabled only while this card's step-3 issue
 * is `needs-verification` (fields complete, unsigned): that is the ONE state
 * this button can actually clear. Disabled otherwise, with a tooltip that
 * never invents a number this component would have to recompute: a
 * `needs-input` issue's own count is already on screen two rows below, in
 * `SubstanceEditor`'s own "N Pflichtfelder offen" banner (`openCounts`'s own
 * doc: deliberately NOT the same count as a worklist issue's `articleIds`,
 * so this hint points at that banner rather than restating a different
 * number next to it). No issue at all, or a `ready` issue (signed, a guided
 * resolution still awaits Continue elsewhere: `issueReasonKey`'s own doc):
 * the same "nothing to confirm here" hint, since neither is something this
 * button, or anything else in the V3 mask, can act on.
 *
 * On success the card's step-3 issue disappears on its own: `signStep3`'s
 * verify command lands in the SAME condition-fields cache
 * (`useVerifyStatement`'s own `onSuccess`, a synchronous `setQueryData`,
 * not a refetch) that `useDeclarationFlowState` derives the worklist from,
 * so `ReviewMaskPane`'s `step3Issue` recomputes to undefined on the next
 * render with no local override needed here.
 *
 * RIDES THE EDITOR'S SAVE CHAIN (Sentry FRONTEND-3X). A field saves when it
 * loses focus, and the click on this button is what takes the focus away, so
 * the save and the sign-off used to leave together. The server derives the
 * open cells from the STORED rows, so the sign-off named the cells as they
 * were before the save and was refused: 409 STALE_STATE when the save landed
 * while the server derived them, STALE_VIEW when it landed first. The sheet
 * owns the one `useConditionFieldWrites` the editor saves through, and the
 * sign-off runs as a link of its chain (`signAfterSaves`): after the saves
 * queued before it, ahead of any made while it runs (the sheet stays
 * editable). It reads this card's step-3 issue as the mask re-derived it from
 * the saved rows, so it signs the cells the screen shows after the save. An
 * issue the save reopened (`needs-input`) or closed is reported instead of
 * sent, and so is a save that failed: signing would attest the stored value,
 * not the one the reviewer entered.
 */
function ConfirmStep3Button({
  declId,
  framework,
  step3Issue,
  signAfterSaves,
}: {
  declId: string
  framework: string
  step3Issue?: WorklistIssue
  signAfterSaves: ConditionFieldWrites["signAfterSaves"]
}) {
  const { translate } = useTranslation()
  const verifyStatement = useVerifyStatement()
  const conditionFieldsQuery = useConditionRows(declId)
  const { paged } = useReviewMode()
  const [confirming, setConfirming] = useState(false)
  // TUR-1900: `signStep3`'s retry waits end when the sheet closes.
  const abortRef = useRef<AbortController | null>(null)
  useEffect(() => {
    const controller = new AbortController()
    abortRef.current = controller
    return () => controller.abort()
  }, [])
  // The issue as the latest render derived it: read once the saves have
  // landed, never the snapshot taken at the click.
  const latestIssue = useRef(step3Issue)
  useLayoutEffect(() => {
    latestIssue.current = step3Issue
  })

  const enabled = step3Issue?.state === "needs-verification" && !confirming

  const handleConfirm = async () => {
    if (!step3Issue || step3Issue.state !== "needs-verification") return
    setConfirming(true)
    const outcome = await signAfterSaves(async () => {
      // The saves queued before this have their rows in the cache by now (a
      // field save writes them from its response, a substance pick waits for
      // its refetch), and react-query hands them to the review mask on its next
      // notify tick, where this card's issue is derived again. Queued behind
      // that tick, this reads the result.
      await new Promise<void>((resolve) => notifyManager.schedule(resolve))
      const issue = latestIssue.current
      if (issue?.state !== "needs-verification")
        return issue?.state === "needs-input" ? "needs-input" : "nothing"
      return signStep3(verifyStatement, {
        declId,
        statementId: issue.statementId ?? issue.unitId,
        framework,
        state: issue.state,
        articleIds: issue.articleIds,
        rows: conditionFieldsQuery.data?.rows ?? [],
        saveOrder: paged,
        // TUR-1900: the retry waits run inside this link of the save chain, so
        // no save overlaps a retry either; closing the sheet ends them.
        signal: abortRef.current?.signal,
      })
    })
    if (
      typeof outcome === "object" &&
      !outcome.ok &&
      outcome.reason === "aborted"
    )
      return
    setConfirming(false)
    if (outcome === "unsaved")
      toast.error(translate("MC_DECL_V2_SUBST_CONFIRM_UNSAVED"))
    else if (outcome === "needs-input")
      toast.error(translate("MC_DECL_V2_SUBST_CONFIRM_MISSING_HINT"))
    else if (outcome === "nothing")
      toast.error(translate("MC_DECL_V2_SUBST_CONFIRM_NOTHING_HINT"))
    else if (outcome.ok)
      toast.success(translate("MC_DECL_V2_SUBST_CONFIRM_SUCCESS"))
    else if (outcome.reason === "stale-view")
      toast.error(translate("MC_DECL_V2_SUBST_CONFIRM_STALE"))
    else if (outcome.reason === "nothing-to-verify")
      toast.error(translate("MC_DECL_V2_SUBST_CONFIRM_NOTHING"))
    else toast.error(translate("MC_DECL_V2_SUBST_CONFIRM_FAILED"))
  }

  const button = (
    <button
      type="button"
      className="sv2-hact"
      disabled={!enabled}
      onClick={() => void handleConfirm()}
    >
      <BusyLabel busy={confirming}>
        {translate("MC_DECL_V2_SUBST_CONFIRM_BUTTON")}
      </BusyLabel>
    </button>
  )
  // Mid-confirm the button stays as it is, spinner and all: the hint below
  // explains a button that cannot be pressed, not one that is at work.
  if (enabled || confirming) return button

  // The wrapping span carries the tooltip, not the button itself: a
  // `disabled` native button never fires hover/focus listeners a Radix
  // trigger relies on (same reasoning `ReviewMaskPane`'s own disabled-button
  // tooltip documents for `.sv2-act[disabled]`).
  const hint =
    step3Issue?.state === "needs-input"
      ? "MC_DECL_V2_SUBST_CONFIRM_MISSING_HINT"
      : "MC_DECL_V2_SUBST_CONFIRM_NOTHING_HINT"
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">{button}</span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[280px]">
          {translate(hint)}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
